<template>
    <div>
        <DataTable ref="horseTable" :value="horses" lazy paginator dataKey="id"
                   v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset" removableSort
                   :totalRecords="totalRecords" :loading="loading" @page="onPage"
                   @sort="onPage" :rowsPerPageOptions="[5,10,20,50]"
                   selection-mode="single" v-model:selection="horse" @row-select="onRowSelect"
                   filterDisplay="row" v-model:filters="searchCriteria" scrollable
                   scrollHeight="calc(100vh - 300px)">
            <Column field="studbookRegistryId" :header="$t('message.horseList.table.studbookRegistryId')"
                    :sortable="true" filterField="studbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #body="{ data }">
                    {{ data.studbookRegistryId }}
                </template>
                <template #filter>
                    <CustomInputText name="studbookRegistryId" :maxLength="9" v-model="searchCriteria.studbookRegistryId.value"
                                     @update:modelValue="search" />
                </template>
            </Column>
            <Column field="ueln" :header="$t('message.horseList.table.ueln')" :sortable="true" filterField="ueln"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.ueln }}
                </template>
                <template #filter>
                    <CustomInputText name="ueln" :maxLength="15" v-model="searchCriteria.ueln.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="name" :header="$t('message.horseList.table.name')" :sortable="true" filterField="name"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.name }}
                </template>
                <template #filter>
                    <CustomInputText name="name" v-model="searchCriteria.name.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="horseSexName" :header="$t('message.horseList.table.gender')" :sortable="true" filterField="sex" sortField="sex"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #body="{ data }">
                    {{ data.horseSexName }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseSex name="sex" v-model="searchCriteria.sex.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="coatColor" :header="$t('message.horseList.table.coatColor')" :sortable="true" sortField="color.shortName" filterField="coatColor"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.coatColor }}
                </template>
                <template #filter>
                    <CustomMultiSelectHorseColor name="coatColorIds" v-model="searchCriteria.coatColorIds.value" item-label="name"
                                                 @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="dateOfBirth" :header="$t('message.horseList.table.dateOfBirth')" :sortable="true" filterField="dateOfBirth"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.dateOfBirth }}
                </template>
                <template #filter>
                    <CustomCalendar name="dateOfBirth" v-model="searchCriteria.dateOfBirth.value" selectionMode="range"
                                    :showTime="false" :placeholder="$t('message.horseList.yearOrRange')" dateFormat="yy" view="year" showButtonBar
                                    @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="previousStudbookRegistryId" :header="$t('message.horseList.table.originalStudbookRef')" :sortable="true" filterField="previousStudbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.previousStudbookRegistryId }}
                </template>
                <template #filter>
                    <CustomInputText name="previousStudbookRegistryId" v-model="searchCriteria.previousStudbookRegistryId.value" @update:modelValue="search" />
                </template>
            </Column>
            <template #empty>
                {{ $t('message.other.emptyTable') }}
            </template>
        </DataTable>
    </div>
</template>

<script>
    import Button from "primevue/button";
    import DataTable from "primevue/datatable";
    import Column from "primevue/column";
    import Calendar from "primevue/calendar";
    import {
        getHorseView, searchHorse, searchHorseCount,
    } from "@/swagger/vue-api-client";
    import CustomInputText from "@/components/form/CustomInputText.vue";
    import CustomCalendar from "@/components/form/CustomCalendar.vue";
    import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
    import CustomMultiSelectHorseSex from "@/components/form/CustomMultiSelectHorseSex.vue";
    import CustomButton from "@/components/form/CustomButton.vue";
    import {ScrollToUtils} from "@/utils/ScrollToUtils";
    import CustomSelectOneYesNo from "@/components/form/CustomSelectOneYesNo.vue";
    import {SearchCriteria} from "@/utils/SearchCriteria";

    export default {
        name: "HorseTable",
        components: {
            CustomSelectOneYesNo,
            CustomButton,
            CustomMultiSelectHorseSex,
            CustomMultiSelectHorseColor,
            CustomCalendar,
            CustomInputText,
            Button,
            Column,
            DataTable,
            Calendar,
        },

        props: {
            modelValue: {
                type: Object,
                required: true,
            },
            selectedHorse: {
                type: Object,
                required: true,
            },
        },

        computed: {
            horse: {
                get() { return this.selectedHorse; },
                set(value) { this.$emit("update:selectedHorse", value); },
            },
            searchCriteria: {
                get() { return this.modelValue; },
                set(value) { this.$emit("update:modelValue", value); },
            },
        },

        data() {
            return {
                loading: false,
                totalRecords: 0,
                horses: [],
            };
        },

        emits: ["update:modelValue", "update:selectedHorse", "showForm"],

        mounted() {
            this.search();
        },

        methods: {
            search() {
                this.onPage(this.getFirstPage());
                // this.updateTotalRecords();
            },

            onPage(event) {
                this.loading = true;

                this.searchCriteria.page.offset = event.first;
                this.searchCriteria.page.limit = event.rows;
                this.searchCriteria.page.sortField = event.sortField;
                this.searchCriteria.page.sortOrder = event.sortOrder;

                const body = SearchCriteria.prepareCriteria(this.searchCriteria, true);

                searchHorse({body}).then((response) => {
                    this.horses = response.data;
                    return searchHorseCount({body})
                        .then((response) => {
                            this.totalRecords = response.data;
                            this.loading = false;
                        });
                });
            },

            getFirstPage() {
                return {
                    first: this.searchCriteria.page.offset,
                    rows: this.searchCriteria.page.limit,
                };
            },

            onRowSelect(event) {
                ScrollToUtils.scrollToElement();
                this.$emit("showForm");
                getHorseView({id: event.data.id}).then((response) => {
                    this.horse = response.data;
                });
            },
        },

        watch: {
            searchCriteria: {
                handler(value) {
                    this.$emit("update:modelValue", value);
                },
                deep: true,
            },
        },
    };
</script>

<style lang="less" scoped>

</style>
